import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * AutoTracker — automatisk PTZ-utsnitt for seiersintervjuer (Sony BRC, VISCA over IP).
 *
 * Arkitektur:
 *  - Videobildet hentes fra capture-kort (getUserMedia).
 *  - MediaPipe PoseLandmarker (inntil 2 personer) kjører i nettleseren.
 *  - Onsket utsnitt: mage (hofter) til over hodetoppen.
 *  - Pan/tilt/zoom-korreksjoner sendes som VISCA-hex til en lokal bro
 *    (visca_bridge.py pa http://127.0.0.1:52380) som videresender UDP til kameraet.
 */

const BRIDGE_URL = 'http://127.0.0.1:52380';
const MP_WASM = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm';
const MP_MODEL = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';

// Utsnitt-mal for intervju: god headroom, motivets senter litt over midten.
const TARGET_TOP = 0.12;
const TARGET_BOTTOM = 0.90;
const TARGET_SPAN = TARGET_BOTTOM - TARGET_TOP;          // 0.78
const CENTER_Y = 0.52;                                   // vertikalt mal for motivets senter
// Smart zoom: hold HELE motivet innenfor med margin — bade i hoyde og bredde.
const ZOOM_OUT_H = 0.86;   // motivet for hoyt -> zoom ut
const ZOOM_OUT_W = 0.78;   // motivet for bredt (personer langt fra hverandre) -> zoom ut
const ZOOM_IN_H = 0.58;    // motivet for lite i hoyde ...
const ZOOM_IN_W = 0.52;    // ... OG for lite i bredde -> zoom inn
// Demping (lavpassfilter): lavere = roligere, mer filmaktig bevegelse.
const SMOOTH = 0.16;

// Hastighetsprofiler: pan-speed ved stor/middels/liten feil + tilt + zoom-speed-hex
const SPEED_PROFILES = {
  1: { label: 'Rolig',  panFar: 3, panMid: 2, panNear: 1, tiltFar: 2, tiltNear: 1, zoomIn: '8101040720FF', zoomOut: '8101040730FF' },
  2: { label: 'Normal', panFar: 5, panMid: 3, panNear: 2, tiltFar: 4, tiltNear: 2, zoomIn: '8101040721FF', zoomOut: '8101040731FF' },
  3: { label: 'Rask',   panFar: 8, panMid: 5, panNear: 3, tiltFar: 6, tiltNear: 3, zoomIn: '8101040722FF', zoomOut: '8101040732FF' },
};

export const AutoTracker = ({ onTrackingChange }) => {
  const [bridgeOk, setBridgeOk] = useState(false);
  const [bridgeVersion, setBridgeVersion] = useState(0);
  const [cameraIp, setCameraIp] = useState(() => {
    const v = localStorage.getItem('autotrack-camera-ip');
    return (!v || v === '192.168.1.100') ? '10.52.49.254' : v;
  });
  const [invertPan, setInvertPan] = useState(() => localStorage.getItem('autotrack-invert-pan') === '1');
  const [invertTilt, setInvertTilt] = useState(() => localStorage.getItem('autotrack-invert-tilt') === '1');
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(() => localStorage.getItem('autotrack-device-id') || '');
  const [videoOn, setVideoOn] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const [statusText, setStatusText] = useState('');

  // Folsomhet (persistert)
  const [deadZone, setDeadZone] = useState(() => parseInt(localStorage.getItem('autotrack-deadzone') || '7', 10));
  const [speedLevel, setSpeedLevel] = useState(() => parseInt(localStorage.getItem('autotrack-speed') || '2', 10));
  const [returnHomeSec, setReturnHomeSec] = useState(() => parseInt(localStorage.getItem('autotrack-home-sec') || '4', 10));
  const [personCount, setPersonCount] = useState(() => parseInt(localStorage.getItem('autotrack-persons') || '2', 10));
  const settingsRef = useRef({ deadZone: 7, speedLevel: 2, returnHomeSec: 4, personCount: 2 });
  useEffect(() => {
    settingsRef.current = { deadZone, speedLevel, returnHomeSec, personCount };
    localStorage.setItem('autotrack-deadzone', String(deadZone));
    localStorage.setItem('autotrack-speed', String(speedLevel));
    localStorage.setItem('autotrack-home-sec', String(returnHomeSec));
    localStorage.setItem('autotrack-persons', String(personCount));
  }, [deadZone, speedLevel, returnHomeSec, personCount]);

  // Zoom-grenser (lagres i broen — håndheves for alle kommandoer)
  const [zoomMax, setZoomMax] = useState(null);
  const [zoomMin, setZoomMin] = useState(null);
  const [zoomPos, setZoomPos] = useState(null);
  const zoomLimitsRef = useRef({ max: null, min: null });
  const zoomPosRef = useRef(null);
  useEffect(() => { zoomLimitsRef.current = { max: zoomMax, min: zoomMin }; }, [zoomMax, zoomMin]);
  useEffect(() => {
    if (!bridgeOk || bridgeVersion < 4) return;
    fetch(`${BRIDGE_URL}/limits?ip=${encodeURIComponent(cameraIp.trim())}`)
      .then(r => r.json())
      .then(d => { setZoomMax(d.max ?? null); setZoomMin(d.min ?? null); })
      .catch(() => {});
  }, [bridgeOk, bridgeVersion, cameraIp]);

  const setLimitHere = useCallback(async (which) => {
    try {
      const r = await fetch(`${BRIDGE_URL}/limits/set-here`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip: cameraIp.trim(), which }),
      });
      const d = await r.json();
      if (d.ok) {
        if (which === 'max') setZoomMax(d.pos); else setZoomMin(d.pos);
        setStatusText(`${which === 'max' ? 'Maks innzoom' : 'Maks utzoom'} satt til ${Math.round((d.pos / 16384) * 100)}%`);
      } else {
        setStatusText(d.error || 'Kameraet svarte ikke på zoom-spørring — sjekk kamera-IP');
      }
    } catch {
      setStatusText('Krever bro v4 — last ned bro-scriptet på nytt og start det på nytt');
    }
  }, [cameraIp]);

  const clearLimits = useCallback(async () => {
    try {
      await fetch(`${BRIDGE_URL}/limits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip: cameraIp.trim(), max: null, min: null }),
      });
    } catch {}
    setZoomMax(null); setZoomMin(null);
    setStatusText('Zoom-grenser fjernet');
  }, [cameraIp]);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const landmarkerRef = useRef(null);
  const loopRef = useRef(null);
  const lastDriveRef = useRef({ key: '', at: 0 });
  const lastZoomRef = useRef({ key: '', at: 0 });
  const trackingRef = useRef(false);
  const lastSeenRef = useRef(0);
  const atHomeRef = useRef(false);
  const zoomPollRef = useRef(null);
  const smoothRef = useRef({ cx: 0.5, cy: CENTER_Y, w: 0.4, h: 0.6, init: false });

  // --- Bro-helse ---
  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const r = await fetch(`${BRIDGE_URL}/health`, { signal: AbortSignal.timeout(2500) });
        if (alive) {
          setBridgeOk(r.ok);
          if (r.ok) {
            const d = await r.json().catch(() => ({}));
            setBridgeVersion(d.version || 1);
          }
        }
      } catch {
        if (alive) setBridgeOk(false);
      }
    };
    check();
    const t = setInterval(check, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Synkroniser VISCA-sekvensnr én gang pr. IP når broen er tilkoblet
  // (ellers ignorerer kameraet kommandoer etter en omstart av broen).
  const resetIpRef = useRef('');
  useEffect(() => {
    const ip = cameraIp.trim();
    if (!bridgeOk || !ip || resetIpRef.current === ip) return;
    resetIpRef.current = ip;
    fetch(`${BRIDGE_URL}/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ip, port: 52381 }),
    }).catch(() => {});
  }, [bridgeOk, cameraIp]);

  const sendVisca = useCallback((hex) => {
    fetch(`${BRIDGE_URL}/visca`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ip: cameraIp.trim(), port: 52381, hex }),
    }).catch(() => {});
  }, [cameraIp]);

  const stopAllMotion = useCallback(() => {
    sendVisca('8101060118180303FF'); // pan/tilt stop
    sendVisca('8101040700FF');       // zoom stop
    lastDriveRef.current = { key: '', at: 0 };
    lastZoomRef.current = { key: '', at: 0 };
  }, [sendVisca]);

  const saveHome = useCallback(() => {
    sendVisca('8101043F0100FF'); // Memory SET preset 0
    setStatusText('Bordposisjon lagret (preset 0)');
  }, [sendVisca]);

  // Les kameraets zoom-posisjon via inquiry (krever bro v2)
  const queryZoomPos = useCallback(async () => {
    try {
      const r = await fetch(`${BRIDGE_URL}/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip: cameraIp.trim(), port: 52381, hex: '81090447FF' }),
        signal: AbortSignal.timeout(1500),
      });
      const d = await r.json();
      if (d.ok && d.reply && d.reply.length >= 12) {
        // Svar: 90 50 0p 0q 0r 0s FF -> posisjon pqrs
        const h = d.reply;
        const pos = (parseInt(h[5], 16) << 12) | (parseInt(h[7], 16) << 8) | (parseInt(h[9], 16) << 4) | parseInt(h[11], 16);
        zoomPosRef.current = pos;
        setZoomPos(pos);
        return pos;
      }
    } catch {}
    return null;
  }, [cameraIp]);

  const recallHome = useCallback(() => {
    sendVisca('8101060118180303FF'); // stopp bevegelse forst
    sendVisca('8101040700FF');
    sendVisca('8101043F0200FF'); // Memory RECALL preset 0
  }, [sendVisca]);

  // --- Videoenheter ---
  const refreshDevices = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true }).then(s => s.getTracks().forEach(t => t.stop()));
    } catch {}
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices(all.filter(d => d.kind === 'videoinput'));
    } catch {}
  }, []);

  useEffect(() => { refreshDevices(); }, [refreshDevices]);

  const startVideo = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: deviceId ? { exact: deviceId } : undefined, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setVideoOn(true);
      setStatusText('');
    } catch (e) {
      setStatusText('Fikk ikke tilgang til videoenheten: ' + (e?.message || e));
    }
  }, [deviceId]);

  const stopVideo = useCallback(() => {
    stopTracking();
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
    setVideoOn(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Tracking-logikk ---
  const analyse = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const lm = landmarkerRef.current;
    if (!video || !canvas || !lm || video.readyState < 2) return;

    const result = lm.detectForVideo(video, performance.now());
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const W = canvas.width, H = canvas.height;
    const { deadZone: dzPct, speedLevel: spdLvl, returnHomeSec: homeSec, personCount: maxPersons } = settingsRef.current;
    const dzX = dzPct / 100;
    const dzY = (dzPct + 1) / 100;
    const spd = SPEED_PROFILES[spdLvl] || SPEED_PROFILES[2];

    // Guidelinjer for malutsnittet
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.setLineDash([6, 6]);
    ctx.strokeRect(0.5 * W - dzX * W, TARGET_TOP * H, dzX * 2 * W, TARGET_SPAN * H);
    ctx.setLineDash([]);

    const persons = [];
    (result?.landmarks || []).slice(0, 2).forEach(marks => {
      const head = marks.slice(0, 11);
      const headTop = Math.min(...head.map(p => p.y));
      const hipY = ((marks[23]?.y ?? 1) + (marks[24]?.y ?? 1)) / 2;
      const xs = [11, 12, 23, 24].map(i => marks[i]?.x).filter(x => x != null);
      if (!xs.length) return;
      persons.push({
        top: Math.max(0, headTop - 0.05),
        bottom: Math.min(1, hipY),
        left: Math.min(...xs),
        right: Math.max(...xs),
      });
    });

    // 1-personmodus: folg kun den storste personen i bildet (hovedmotivet)
    if (maxPersons === 1 && persons.length > 1) {
      persons.sort((a, b) => (b.bottom - b.top) - (a.bottom - a.top));
      persons.length = 1;
    }

    let driveKey = 'stop';
    let driveHex = '8101060118180303FF';
    let zoomKey = 'stop';
    let zoomHex = '8101040700FF';
    let info = 'Ingen personer funnet — holder posisjon';
    let sendCommands = true;
    const now = Date.now();

    if (persons.length > 0) {
      lastSeenRef.current = now;
      atHomeRef.current = false;
      const top = Math.min(...persons.map(p => p.top));
      const bottom = Math.max(...persons.map(p => p.bottom));
      const left = Math.min(...persons.map(p => p.left));
      const right = Math.max(...persons.map(p => p.right));

      // Rå personbokser (turkis) — direkte fra deteksjonen
      ctx.strokeStyle = 'rgba(92,224,214,0.9)';
      ctx.lineWidth = 2;
      persons.forEach(p => ctx.strokeRect(p.left * W, p.top * H, (p.right - p.left) * W, (p.bottom - p.top) * H));

      // Rå samleramme
      const rawCx = (left + right) / 2;
      const rawCy = (top + bottom) / 2;
      const rawW = right - left;
      const rawH = bottom - top;

      // Kraftig lavpassdemping -> filmaktig, aldri hakkete
      const s = smoothRef.current;
      if (!s.init) { s.cx = rawCx; s.cy = rawCy; s.w = rawW; s.h = rawH; s.init = true; }
      else {
        s.cx += (rawCx - s.cx) * SMOOTH;
        s.cy += (rawCy - s.cy) * SMOOTH;
        s.w  += (rawW  - s.w)  * SMOOTH;
        s.h  += (rawH  - s.h)  * SMOOTH;
      }
      const cx = s.cx, cy = s.cy, span = s.h, wide = s.w;

      // Dempet samleramme (gul) = det kameraet faktisk styrer mot
      ctx.strokeStyle = '#FFD700';
      ctx.lineWidth = 3;
      ctx.strokeRect((cx - wide / 2) * W, (cy - span / 2) * H, wide * W, span * H);

      const ex = cx - 0.5;
      const ey = cy - CENTER_Y;

      // Proporsjonal pan/tilt: farten skaleres jevnt med avviket (rolig nær mål)
      let panDir = 3, panSpd = 0;
      if (Math.abs(ex) > dzX) {
        panDir = ex > 0 ? 2 : 1; // 2 = hoyre, 1 = venstre
        if (invertPan) panDir = panDir === 2 ? 1 : 2;
        const norm = Math.min(1, Math.max(0, Math.abs(ex) - dzX) / Math.max(0.05, 0.35 - dzX));
        panSpd = Math.max(1, Math.round(norm * spd.panFar));
      }
      let tiltDir = 3, tiltSpd = 0;
      if (Math.abs(ey) > dzY) {
        tiltDir = ey > 0 ? 2 : 1; // 2 = ned, 1 = opp
        if (invertTilt) tiltDir = tiltDir === 2 ? 1 : 2;
        const norm = Math.min(1, Math.max(0, Math.abs(ey) - dzY) / Math.max(0.05, 0.30 - dzY));
        tiltSpd = Math.max(1, Math.round(norm * spd.tiltFar));
      }
      if (panDir !== 3 || tiltDir !== 3) {
        const vv = String(Math.max(panSpd, 1)).padStart(2, '0');
        const ww = String(Math.max(tiltSpd, 1)).padStart(2, '0');
        driveKey = `d-${panDir}-${tiltDir}-${vv}-${ww}`;
        driveHex = `81010601${vv}${ww}0${panDir === 3 ? 3 : panDir}0${tiltDir === 3 ? 3 : tiltDir}FF`;
      }

      // Smart zoom: bruk BÅDE høyde og bredde. Zoom ut når to personer står
      // langt fra hverandre (stor bredde) eller motivet nærmer seg kanten.
      if (span > ZOOM_OUT_H || wide > ZOOM_OUT_W) { zoomKey = 'wide'; zoomHex = spd.zoomOut; }
      else if (span < ZOOM_IN_H && wide < ZOOM_IN_W) { zoomKey = 'tele'; zoomHex = spd.zoomIn; }

      // Handhev zoom-grenser (hvis satt og posisjon er kjent)
      const zl = zoomLimitsRef.current;
      const zp = zoomPosRef.current;
      if (zp != null) {
        if (zoomKey === 'tele' && zl.max != null && zp >= zl.max) { zoomKey = 'stop'; zoomHex = '8101040700FF'; }
        if (zoomKey === 'wide' && zl.min != null && zp <= zl.min) { zoomKey = 'stop'; zoomHex = '8101040700FF'; }
      }

      const parts = [];
      parts.push(`${persons.length} person${persons.length > 1 ? 'er' : ''}`);
      if (panDir === 1) parts.push('pan ←'); if (panDir === 2) parts.push('pan →');
      if (tiltDir === 1) parts.push('tilt ↑'); if (tiltDir === 2) parts.push('tilt ↓');
      if (zoomKey === 'tele') parts.push('zoom inn'); if (zoomKey === 'wide') parts.push('zoom ut');
      if (parts.length === 1) parts.push('utsnitt OK');
      info = parts.join(' · ');
    } else if (atHomeRef.current) {
      // Star allerede i bordposisjon — ikke send noe, bare vent
      smoothRef.current.init = false;
      info = 'Bordposisjon — venter på neste intervju';
      sendCommands = false;
    } else if (lastSeenRef.current > 0 && now - lastSeenRef.current > homeSec * 1000) {
      // Intervjuet er ferdig — kjor kameraet tilbake til bordet
      smoothRef.current.init = false;
      recallHome();
      atHomeRef.current = true;
      lastDriveRef.current = { key: '', at: 0 };
      lastZoomRef.current = { key: '', at: 0 };
      info = 'Kjører til bordposisjon — venter på neste intervju';
      sendCommands = false;
    } else {
      // Ingen personer akkurat nå — nullstill demping så neste gjenkjenning snapper pent
      smoothRef.current.init = false;
    }

    setStatusText(info);
    if (!sendCommands) return;

    const ld = lastDriveRef.current;
    if (driveKey !== ld.key || now - ld.at > 700) {
      sendVisca(driveHex);
      lastDriveRef.current = { key: driveKey, at: now };
    }
    const lz = lastZoomRef.current;
    if (zoomKey !== lz.key || now - lz.at > 700) {
      sendVisca(zoomHex);
      lastZoomRef.current = { key: zoomKey, at: now };
    }
  }, [sendVisca, invertPan, invertTilt, recallHome]);

  const startTracking = useCallback(async () => {
    if (!videoOn) { setStatusText('Start kameraet forst'); return; }
    if (!landmarkerRef.current) {
      setModelLoading(true);
      setStatusText('Laster persondeteksjon…');
      try {
        const { FilesetResolver, PoseLandmarker } = await import('@mediapipe/tasks-vision');
        const fileset = await FilesetResolver.forVisionTasks(MP_WASM);
        landmarkerRef.current = await PoseLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: MP_MODEL, delegate: 'GPU' },
          runningMode: 'VIDEO',
          numPoses: 2,
        });
      } catch (e) {
        setModelLoading(false);
        setStatusText('Kunne ikke laste deteksjonsmodellen: ' + (e?.message || e));
        return;
      }
      setModelLoading(false);
    }
    lastSeenRef.current = Date.now();
    atHomeRef.current = false;
    trackingRef.current = true;
    setTracking(true);
    if (onTrackingChange) onTrackingChange(true);
    loopRef.current = setInterval(() => { if (trackingRef.current) analyse(); }, 140);
    queryZoomPos();
    zoomPollRef.current = setInterval(queryZoomPos, 600);
  }, [videoOn, analyse, onTrackingChange, queryZoomPos]);

  const stopTracking = useCallback(() => {
    trackingRef.current = false;
    setTracking(false);
    if (onTrackingChange) onTrackingChange(false);
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
    if (zoomPollRef.current) { clearInterval(zoomPollRef.current); zoomPollRef.current = null; }
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    stopAllMotion();
    setStatusText('');
  }, [stopAllMotion, onTrackingChange]);

  useEffect(() => () => {
    trackingRef.current = false;
    if (loopRef.current) clearInterval(loopRef.current);
    if (zoomPollRef.current) clearInterval(zoomPollRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (landmarkerRef.current) { try { landmarkerRef.current.close(); } catch {} }
  }, []);

  // Fjernstyring: hent relé-nøkkel
  const [relayToken, setRelayToken] = useState('');
  const [copied, setCopied] = useState('');
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ptz-relay/config`)
      .then(r => r.json())
      .then(d => setRelayToken(d.token || ''))
      .catch(() => {});
  }, []);
  const copyText = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(''), 1500);
    }).catch(() => {});
  };
  const mobileUrl = relayToken ? `${window.location.origin}/ptz-mobil?key=${relayToken}` : '';
  const bridgeCmd = relayToken ? `python visca_bridge.py ${window.location.origin} ${relayToken} ${cameraIp.trim() || '<kamera-ip>'}` : '';

  // Del kamera-IP med skyserveren, slik at mobil-kommandoer alltid har riktig mål
  useEffect(() => {
    if (!relayToken || !cameraIp.trim()) return;
    const t = setTimeout(() => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ptz-relay/camera-ip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: relayToken, ip: cameraIp.trim() }),
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [relayToken, cameraIp]);

  // Returfeed: send stillbilder til skyen (~20 fps via WebSocket, HTTP-fallback ~1 fps)
  useEffect(() => {
    if (!videoOn || !relayToken) return;
    let ws = null;
    let closed = false;
    const wsUrl = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws')}/api/ws/ptz-frames?token=${encodeURIComponent(relayToken)}&role=pub`;
    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(wsUrl);
        ws.onclose = () => { if (!closed) setTimeout(connect, 2000); };
        ws.onerror = () => {};
      } catch {}
    };
    connect();
    const canvas = document.createElement('canvas');
    let lastHttp = 0;
    const t = setInterval(() => {
      const v = videoRef.current;
      if (!v || v.readyState < 2 || !v.videoWidth) return;
      canvas.width = 480;
      canvas.height = Math.round(480 * v.videoHeight / v.videoWidth);
      canvas.getContext('2d').drawImage(v, 0, 0, canvas.width, canvas.height);
      const img = canvas.toDataURL('image/jpeg', 0.45);
      if (ws && ws.readyState === 1) {
        if (ws.bufferedAmount < 300000) ws.send(img);
      } else if (Date.now() - lastHttp > 1000) {
        lastHttp = Date.now();
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ptz-relay/frame`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: relayToken, image: img }),
        }).catch(() => {});
      }
    }, 50);
    return () => { closed = true; clearInterval(t); if (ws) try { ws.close(); } catch {} };
  }, [videoOn, relayToken]);

  const manualBtn = (label, hexDown, testId) => (
    <button
      data-testid={testId}
      onPointerDown={() => sendVisca(hexDown)}
      onPointerUp={() => sendVisca('8101060118180303FF')}
      onPointerLeave={() => sendVisca('8101060118180303FF')}
      style={{
        width: '34px', height: '34px', borderRadius: '6px', cursor: 'pointer',
        border: '1px solid #334155', backgroundColor: '#1E293B', color: '#E2E8F0', fontSize: '0.9rem',
      }}
    >
      {label}
    </button>
  );

  const S = {
    label: { fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#64748B', marginBottom: '4px' },
    input: {
      width: '100%', background: '#1E293B', border: '1px solid #334155', color: '#E2E8F0',
      fontSize: '0.78rem', padding: '6px 10px', borderRadius: '4px', fontFamily: "'JetBrains Mono', monospace",
      outline: 'none', boxSizing: 'border-box',
    },
  };

  return (
    <div data-testid="autotracker-panel" style={{ padding: '12px', color: '#E2E8F0', fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @keyframes autotrack-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
      `}</style>

      {/* Tracking-varsel */}
      <div
        data-testid="autotrack-indicator"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          padding: '9px', borderRadius: '6px', marginBottom: '10px',
          fontFamily: "'Barlow Condensed', sans-serif", fontSize: '0.95rem', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.1em',
          backgroundColor: tracking ? 'rgba(34,197,94,0.15)' : 'rgba(100,116,139,0.12)',
          border: `1px solid ${tracking ? 'rgba(34,197,94,0.5)' : 'rgba(100,116,139,0.3)'}`,
          color: tracking ? '#22C55E' : '#64748B',
        }}
      >
        <span style={{
          width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
          backgroundColor: tracking ? '#22C55E' : '#64748B',
          animation: tracking ? 'autotrack-pulse 1.2s ease-in-out infinite' : 'none',
        }} />
        {tracking ? 'Tracking aktiv' : 'Tracking av'}
      </div>

      {/* Bro-status */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
        borderRadius: '6px', backgroundColor: bridgeOk ? 'rgba(34,197,94,0.1)' : 'rgba(220,38,38,0.1)',
        border: `1px solid ${bridgeOk ? 'rgba(34,197,94,0.35)' : 'rgba(220,38,38,0.35)'}`, marginBottom: '10px',
      }}>
        <span style={{
          width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0,
          backgroundColor: bridgeOk ? '#22C55E' : '#DC2626',
        }} />
        <span data-testid="bridge-status" style={{ fontSize: '0.75rem', flex: 1 }}>
          {bridgeOk ? 'VISCA-bro tilkoblet' : 'VISCA-bro ikke funnet'}
        </span>
        <a
          href="/assets/visca_bridge.py"
          download="visca_bridge.py"
          data-testid="bridge-download-link"
          style={{ fontSize: '0.7rem', color: '#5CE0D6', textDecoration: 'underline', whiteSpace: 'nowrap' }}
        >
          Last ned bro
        </a>
      </div>
      {!bridgeOk && (
        <div style={{ fontSize: '0.68rem', color: '#94A3B8', lineHeight: 1.5, marginBottom: '10px' }}>
          Last ned <b>visca_bridge.py</b> og kjør <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>python visca_bridge.py</span> på
          denne PC-en (krever kun Python). Broen sender kommandoene videre til kameraet på lokalnettet.
        </div>
      )}

      {/* Kamera-IP + invertering */}
      <div style={{ marginBottom: '10px' }}>
        <div style={S.label}>Kameraets IP-adresse (VISCA over IP, port 52381)</div>
        <input
          data-testid="autotrack-camera-ip"
          value={cameraIp}
          onChange={e => { setCameraIp(e.target.value); localStorage.setItem('autotrack-camera-ip', e.target.value); }}
          placeholder="10.52.49.254"
          style={S.input}
        />
        <div style={{ display: 'flex', gap: '14px', marginTop: '6px' }}>
          <label style={{ fontSize: '0.7rem', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
            <input type="checkbox" checked={invertPan} data-testid="autotrack-invert-pan"
              onChange={e => { setInvertPan(e.target.checked); localStorage.setItem('autotrack-invert-pan', e.target.checked ? '1' : '0'); }} />
            Inverter pan
          </label>
          <label style={{ fontSize: '0.7rem', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
            <input type="checkbox" checked={invertTilt} data-testid="autotrack-invert-tilt"
              onChange={e => { setInvertTilt(e.target.checked); localStorage.setItem('autotrack-invert-tilt', e.target.checked ? '1' : '0'); }} />
            Inverter tilt
          </label>
        </div>
      </div>

      {/* Manuell test */}
      <div style={{ marginBottom: '10px' }}>
        <div style={S.label}>Test kamerakontroll (hold inne)</div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {manualBtn('◀', '810106010C0A0103FF', 'autotrack-test-left')}
          {manualBtn('▶', '810106010C0A0203FF', 'autotrack-test-right')}
          {manualBtn('▲', '810106010C0A0301FF', 'autotrack-test-up')}
          {manualBtn('▼', '810106010C0A0302FF', 'autotrack-test-down')}
          {manualBtn('+', '8101040724FF', 'autotrack-test-zoomin')}
          {manualBtn('−', '8101040734FF', 'autotrack-test-zoomout')}
        </div>
      </div>

      {/* Bordposisjon (hjem) */}
      <div style={{ marginBottom: '10px' }}>
        <div style={S.label}>Bordposisjon (kameraets ventepunkt mellom intervjuer)</div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            data-testid="autotrack-save-home"
            onClick={saveHome}
            style={{
              flex: 1, padding: '8px', borderRadius: '6px', cursor: 'pointer',
              border: '1px solid #334155', backgroundColor: '#1E293B', color: '#E2E8F0',
              fontSize: '0.72rem', fontWeight: 600,
            }}
          >
            Lagre bordposisjon
          </button>
          <button
            data-testid="autotrack-goto-home"
            onClick={() => { recallHome(); atHomeRef.current = true; }}
            style={{
              flex: 1, padding: '8px', borderRadius: '6px', cursor: 'pointer',
              border: '1px solid #334155', backgroundColor: '#1E293B', color: '#5CE0D6',
              fontSize: '0.72rem', fontWeight: 600,
            }}
          >
            Gå til bordposisjon
          </button>
        </div>
        <div style={{ fontSize: '0.62rem', color: '#475569', marginTop: '4px', lineHeight: 1.4 }}>
          Sikt kameraet mot bordet med testknappene, trykk «Lagre bordposisjon». Når bildet har vært
          tomt i {returnHomeSec} sekunder, kjører kameraet automatisk hit og venter på neste intervju.
        </div>
      </div>

      {/* Folsomhet */}
      <div style={{ marginBottom: '10px', padding: '10px', borderRadius: '6px', backgroundColor: '#1E293B', border: '1px solid #334155' }}>
        <div style={{ ...S.label, marginBottom: '8px' }}>Følsomhet</div>
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '0.68rem', color: '#94A3B8', marginBottom: '5px' }}>Antall personer som skal trackes</div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {[1, 2].map(n => (
              <button
                key={n}
                data-testid={`autotrack-persons-${n}`}
                onClick={() => setPersonCount(n)}
                style={{
                  flex: 1, padding: '8px', borderRadius: '999px', cursor: 'pointer',
                  fontSize: '0.72rem', fontWeight: 700,
                  border: personCount === n ? '1.5px solid #5CE0D6' : '1px solid rgba(255,255,255,0.18)',
                  backgroundColor: personCount === n ? 'rgba(92,224,214,0.12)' : 'transparent',
                  color: personCount === n ? '#5CE0D6' : 'rgba(255,255,255,0.55)',
                }}
              >
                {n === 1 ? '1 person' : '2 personer'}
              </button>
            ))}
          </div>
          <div style={{ fontSize: '0.6rem', color: '#475569', marginTop: '4px', lineHeight: 1.4 }}>
            Med «1 person» følges kun den største personen i bildet, selv om flere er synlige.
          </div>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#94A3B8', marginBottom: '3px' }}>
            <span>Dødsone (hvor mye personene kan bevege seg før kameraet følger)</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#E2E8F0' }}>{deadZone}%</span>
          </div>
          <input
            type="range" min="3" max="15" step="1" value={deadZone}
            data-testid="autotrack-deadzone-slider"
            onChange={e => setDeadZone(parseInt(e.target.value, 10))}
            style={{ width: '100%', accentColor: '#5CE0D6' }}
          />
        </div>
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#94A3B8', marginBottom: '3px' }}>
            <span>Bevegelseshastighet</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#E2E8F0' }}>{(SPEED_PROFILES[speedLevel] || SPEED_PROFILES[2]).label}</span>
          </div>
          <input
            type="range" min="1" max="3" step="1" value={speedLevel}
            data-testid="autotrack-speed-slider"
            onChange={e => setSpeedLevel(parseInt(e.target.value, 10))}
            style={{ width: '100%', accentColor: '#5CE0D6' }}
          />
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: '#94A3B8', marginBottom: '3px' }}>
            <span>Ventetid før retur til bordposisjon</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#E2E8F0' }}>{returnHomeSec}s</span>
          </div>
          <input
            type="range" min="2" max="15" step="1" value={returnHomeSec}
            data-testid="autotrack-homesec-slider"
            onChange={e => setReturnHomeSec(parseInt(e.target.value, 10))}
            style={{ width: '100%', accentColor: '#5CE0D6' }}
          />
        </div>
      </div>

      {/* Zoom-grenser */}
      <div style={{ marginBottom: '10px', padding: '10px', borderRadius: '6px', backgroundColor: '#1E293B', border: '1px solid #334155' }}>
        <div style={{ ...S.label, marginBottom: '8px' }}>
          Zoom-grenser
          <span data-testid="autotrack-zoom-pos" style={{ float: 'right', fontFamily: "'JetBrains Mono', monospace", textTransform: 'none', color: zoomPos != null ? '#5CE0D6' : '#475569' }}>
            {zoomPos != null ? `Nå: ${Math.round((zoomPos / 16384) * 100)}%` : 'posisjon ukjent'}
          </span>
        </div>
        {bridgeOk && bridgeVersion < 6 && (
          <div style={{
            fontSize: '0.66rem', color: '#FBBF24', lineHeight: 1.4, marginBottom: '8px',
            padding: '6px 8px', borderRadius: '4px', backgroundColor: 'rgba(251,191,36,0.1)',
            border: '1px solid rgba(251,191,36,0.3)',
          }}>
            Oppdatert bro-script (v6) kreves. Last ned på nytt med
            «Last ned bro»-lenken over og start scriptet på nytt.
          </div>
        )}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
          <button
            data-testid="autotrack-set-zoom-max"
            onClick={() => setLimitHere('max')}
            style={{
              flex: 1, padding: '8px', borderRadius: '6px', cursor: 'pointer',
              border: '1px solid #334155', backgroundColor: '#0F172A', color: '#E2E8F0', fontSize: '0.7rem', fontWeight: 600,
            }}
          >
            Sett maks innzoom her{zoomMax != null ? ` (${Math.round((zoomMax / 16384) * 100)}%)` : ''}
          </button>
          <button
            data-testid="autotrack-set-zoom-min"
            onClick={() => setLimitHere('min')}
            style={{
              flex: 1, padding: '8px', borderRadius: '6px', cursor: 'pointer',
              border: '1px solid #334155', backgroundColor: '#0F172A', color: '#E2E8F0', fontSize: '0.7rem', fontWeight: 600,
            }}
          >
            Sett maks utzoom her{zoomMin != null ? ` (${Math.round((zoomMin / 16384) * 100)}%)` : ''}
          </button>
        </div>
        {(zoomMax != null || zoomMin != null) && (
          <button
            data-testid="autotrack-clear-zoom-limits"
            onClick={clearLimits}
            style={{
              width: '100%', padding: '6px', borderRadius: '6px', cursor: 'pointer',
              border: '1px solid rgba(220,38,38,0.4)', background: 'transparent', color: '#FCA5A5', fontSize: '0.68rem',
            }}
          >
            Fjern zoom-grenser
          </button>
        )}
        <div style={{ fontSize: '0.62rem', color: '#475569', marginTop: '6px', lineHeight: 1.4 }}>
          Zoom kameraet dit du vil ha grensen med +/− testknappene, og trykk «Sett … her».
          Grensene lagres i bro-scriptet og gjelder overalt: autotracker, testknapper og mobil-fjernstyring.
        </div>
      </div>

      {/* Fjernstyring (mobil) */}
      <div style={{ marginBottom: '10px', padding: '10px', borderRadius: '6px', backgroundColor: '#1E293B', border: '1px solid #334155' }}>
        <div style={{ ...S.label, marginBottom: '6px' }}>Fjernstyring fra mobil (send lenken til regi)</div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
          <input
            readOnly
            value={mobileUrl}
            data-testid="autotrack-mobile-url"
            style={{ ...S.input, flex: 1, fontSize: '0.62rem' }}
            onFocus={e => e.target.select()}
          />
          <button
            data-testid="autotrack-copy-mobile-url"
            onClick={() => copyText(mobileUrl, 'url')}
            style={{
              padding: '6px 12px', borderRadius: '4px', border: 'none', cursor: 'pointer',
              backgroundColor: copied === 'url' ? '#22C55E' : '#5CE0D6', color: '#050F23',
              fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap',
            }}
          >
            {copied === 'url' ? 'Kopiert!' : 'Kopier'}
          </button>
        </div>
        <div style={{ fontSize: '0.62rem', color: '#94A3B8', lineHeight: 1.5, marginBottom: '6px' }}>
          For at mobilen skal nå kameraet må broen startes <b>med fjernstyring</b> (v3-script):
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <input
            readOnly
            value={bridgeCmd}
            data-testid="autotrack-bridge-cmd"
            style={{ ...S.input, flex: 1, fontSize: '0.6rem' }}
            onFocus={e => e.target.select()}
          />
          <button
            data-testid="autotrack-copy-bridge-cmd"
            onClick={() => copyText(bridgeCmd, 'cmd')}
            style={{
              padding: '6px 12px', borderRadius: '4px', border: 'none', cursor: 'pointer',
              backgroundColor: copied === 'cmd' ? '#22C55E' : '#5CE0D6', color: '#050F23',
              fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap',
            }}
          >
            {copied === 'cmd' ? 'Kopiert!' : 'Kopier'}
          </button>
        </div>
        <div style={{ fontSize: '0.62rem', color: '#475569', lineHeight: 1.5, marginTop: '6px' }}>
          Returfeed: Når videoen kjører her (Start under Videoenhet), ser mobilen kamerabildet automatisk (~1 bilde/sek).
        </div>
      </div>

      {/* Videoenhet */}
      <div style={{ marginBottom: '10px' }}>
        <div style={S.label}>Videoenhet (capture-kort med kamerabildet)</div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <select
            data-testid="autotrack-device-select"
            value={deviceId}
            onChange={e => { setDeviceId(e.target.value); localStorage.setItem('autotrack-device-id', e.target.value); }}
            style={{ ...S.input, flex: 1 }}
          >
            <option value="">Standard kamera</option>
            {devices.map(d => (
              <option key={d.deviceId} value={d.deviceId}>{d.label || `Enhet ${d.deviceId.slice(0, 6)}`}</option>
            ))}
          </select>
          <button
            data-testid="autotrack-video-toggle"
            onClick={videoOn ? stopVideo : startVideo}
            style={{
              padding: '6px 12px', borderRadius: '4px', border: 'none', cursor: 'pointer',
              backgroundColor: videoOn ? '#DC2626' : '#5CE0D6', color: videoOn ? '#fff' : '#050F23',
              fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap',
            }}
          >
            {videoOn ? 'Stopp' : 'Start'}
          </button>
        </div>
      </div>

      {/* Forhandsvisning med deteksjonsoverlegg */}
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', backgroundColor: '#050505', borderRadius: '6px', overflow: 'hidden', marginBottom: '10px' }}>
        <video ref={videoRef} muted playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
        <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
        {!videoOn && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: '0.75rem' }}>
            Velg videoenhet og trykk Start
          </div>
        )}
      </div>

      {/* Autotrack av/pa */}
      <button
        data-testid="autotrack-toggle"
        onClick={tracking ? stopTracking : startTracking}
        disabled={modelLoading}
        style={{
          width: '100%', padding: '12px', borderRadius: '8px', border: 'none', cursor: 'pointer',
          fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.05rem', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          backgroundColor: tracking ? '#DC2626' : '#22C55E', color: '#fff',
        }}
      >
        {modelLoading ? 'Laster modell…' : tracking ? '■ Stopp autotracking' : '● Start autotracking'}
      </button>

      {statusText && (
        <div data-testid="autotrack-status" style={{
          marginTop: '8px', padding: '7px 10px', borderRadius: '6px',
          backgroundColor: '#1E293B', fontSize: '0.72rem', color: '#94A3B8',
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          {statusText}
        </div>
      )}

      <div style={{ marginTop: '10px', fontSize: '0.64rem', color: '#475569', lineHeight: 1.5 }}>
        Utsnitt: mage → over hodetoppen, inntil 2 personer. Kameraet står i ro når
        utsnittet er innenfor dødsonen (stiplet ramme). Når intervjuet er ferdig og bildet
        er tomt, kjører kameraet tilbake til bordposisjonen og venter på neste intervju.
      </div>
    </div>
  );
};
